import './SelectedIngredients.css';
import { getSelectionCapacityGuidance } from '../utils/capacityGuidance';
import CapacityMark from './CapacityMark';
import { displayIngredientName } from '../utils/displayIngredientName';

function clampPct(value, userMax) {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  const ceiling = (userMax != null && Number.isFinite(userMax)) ? userMax : 100;
  if (value > ceiling) return ceiling;
  return Math.round(value * 100) / 100;
}

function SelectedIngredients({
  selectedIngredients,
  ingredients,
  onRemove,
  // user_ratios extension — only relevant when mode === 'user_ratios'.
  mode = 'auto',
  customPercentages = {},
  onPercentageChange,
}) {
  if (selectedIngredients.length === 0) {
    return null;
  }

  const isUserRatios = mode === 'user_ratios';

  const selectedDetails = selectedIngredients.map((id) => {
    const ingredient = ingredients.find((ing) => ing.id === id);
    return ingredient || { id, name: id };
  });

  const capacityGuidance = getSelectionCapacityGuidance(
    selectedIngredients,
    ingredients
  );

  const handlePctChange = (id, raw, userMax) => {
    if (!onPercentageChange) return;
    const next = clampPct(parseFloat(raw), userMax);
    onPercentageChange(id, next);
  };

  const headingLabel = isUserRatios
    ? `Set Your Proportions (${selectedIngredients.length})`
    : `Selected Ingredients (${selectedIngredients.length})`;

  return (
    <div className="selected-ingredients">
      <h3 className="selected-title">{headingLabel}</h3>
      {isUserRatios && (
        <p className="selected-subtitle">
          Tap a value to type the percentage you'd like.
        </p>
      )}
      <div className="selected-list">
        {selectedDetails.map((ingredient) => {
          const pct = Number(customPercentages[ingredient.id]) || 0;
          const userMax = ingredient.user_max_percent != null ? Number(ingredient.user_max_percent) : null;
          const hasUserMax = userMax != null && Number.isFinite(userMax);
          const atUserMax = isUserRatios && hasUserMax && pct >= userMax - 1e-6;
          const softCap = Number(ingredient.soft_cap_percent);
          const hasSoftCap = Number.isFinite(softCap) && softCap > 0 && softCap < 100;
          const overSoftCap = isUserRatios && hasSoftCap && !atUserMax && pct > softCap + 1e-6;

          return (
            <div
              key={ingredient.id}
              className={`selected-item${isUserRatios ? ' selected-item--ratio' : ''}`}
            >
              <span className="selected-name">
                {displayIngredientName(ingredient.name, ingredient.quality)}
                {ingredient.capacity_tier && (
                  <CapacityMark tier={ingredient.capacity_tier} />
                )}
              </span>

              {isUserRatios && (
                <label
                  className="ratio-input"
                  aria-label={`${ingredient.name} percentage`}
                >
                  <input
                    className="ratio-input__field"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max={hasUserMax ? userMax : 100}
                    step="0.01"
                    value={pct}
                    onChange={(e) => handlePctChange(ingredient.id, e.target.value, userMax)}
                    onFocus={(e) => e.target.select()}
                    aria-label={`${ingredient.name} percent`}
                  />
                  <span className="ratio-input__unit" aria-hidden="true">%</span>
                </label>
              )}

              <button
                className="remove-btn"
                onClick={() => onRemove(ingredient.id)}
                aria-label={`Remove ${ingredient.name}`}
                title="Remove ingredient"
              >
                ×
              </button>

              {atUserMax && (
                <p className="ratio-input__hint ratio-input__hint--capped">
                  Capped at {userMax.toFixed(0)}% — maximum recommended for this ingredient.
                </p>
              )}
              {overSoftCap && (
                <p className="ratio-input__hint">
                  Most blends keep this under {softCap.toFixed(1)}%.
                </p>
              )}
            </div>
          );
        })}
      </div>
      {capacityGuidance && (
        <p className="selected-capacity-hint" role="status">
          {capacityGuidance}
        </p>
      )}
      {selectedIngredients.length < 2 && (
        <p className="selected-hint">
          Select at least 2 ingredients to create a blend
        </p>
      )}
    </div>
  );
}

export default SelectedIngredients;
